import { HttpBackend, HttpRequest, HttpResponse } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable, of } from 'rxjs';
import { catchError, filter, finalize, map, shareReplay, take } from 'rxjs/operators';
import { environment } from '../../../environments/environment';
import { BaseService } from '../base/base.service';

/**
 * Renews an expired session token with the refresh token sign-in issued (POST api/user/refresh), so
 * a session that outlives its token (9 hours) keeps working instead of dropping the user at /login
 * with unsaved work.
 *
 * Only the token and refresh token in the stored user change; nothing else about the user does, so
 * the session hash (SessionUserHashManagerService, which leaves both out) stays the same and other
 * open tabs are not disturbed. A refresh token is single use, so a renewal already made by another
 * tab or another request is picked up from localStorage rather than attempted again.
 */
@Injectable({
  providedIn: 'root'
})
export class TokenRefreshService {

  private inFlight: Observable<string | null> | null = null;

  constructor(
    // The backend, not HttpClient: the renewal call must not pass through the interceptors that
    // would react to its own failure by renewing again.
    private backend: HttpBackend,
    private baseService: BaseService
  ) { }

  /**
   * A token newer than the expired one, or null when the session cannot be renewed (the user is
   * signed out, has no refresh token, or the API refused it).
   */
  public renew(expiredToken: string): Observable<string | null> {
    const user = this.storedUser();
    if (!user?.token) {
      return of(null);
    }
    if (user.token !== expiredToken) {
      return of(user.token);
    }
    if (!user.refreshToken || TokenRefreshService.refreshTokenExpired(user.token)) {
      // Nothing to renew with: asking the API would only be refused (bug 40428).
      return of(null);
    }

    if (!this.inFlight) {
      const body = { token: user.token, refreshToken: user.refreshToken };
      const options = this.baseService.prepareAnonymousOptions(body);
      const request = new HttpRequest('POST', environment.apiUrl + 'api/user/refresh', body, { headers: options.headers });

      this.inFlight = this.backend.handle(request).pipe(
        filter(event => event instanceof HttpResponse),
        take(1),
        map((response: HttpResponse<any>) => this.storeRenewal(response.body)),
        catchError(() => of(this.tokenRenewedElsewhere(expiredToken))),
        finalize(() => this.inFlight = null),
        shareReplay(1)
      );
    }
    return this.inFlight;
  }

  /**
   * Session token lifetime and refresh token lifetime as the API issues them (UserController: Expires = now + 9h;
   * UserService.RefreshTokenLifetime = 24h). Both are issued together at sign-in and at each renewal, so a
   * refresh token expires 15 hours after the session token it came with.
   */
  static readonly refreshOutlivesTokenMs = (24 - 9) * 60 * 60 * 1000;

  /** Renew this long before the session token's expiry, so a request never leaves with a token about to lapse. */
  static readonly expirySkewMs = 30 * 1000;

  /** The session token's expiry from its exp claim; null when the token cannot be read. */
  static tokenExpiry(token: string | null | undefined): number | null {
    try {
      const payload = token?.split('.')[1];
      if (!payload) return null;
      const json = JSON.parse(atob(payload.replace(/-/g, '+').replace(/_/g, '/')));
      return typeof json?.exp === 'number' ? json.exp * 1000 : null;
    } catch {
      return null;
    }
  }

  /** True when the session token has expired, or is about to (bug 40427). False when it cannot be read. */
  static tokenExpired(token: string | null | undefined, now = Date.now()): boolean {
    const expiry = TokenRefreshService.tokenExpiry(token);
    return expiry !== null && expiry - TokenRefreshService.expirySkewMs <= now;
  }

  /** True when the refresh token issued with this session token is past its lifetime (bug 40428). */
  static refreshTokenExpired(token: string | null | undefined, now = Date.now()): boolean {
    const expiry = TokenRefreshService.tokenExpiry(token);
    return expiry !== null && expiry + TokenRefreshService.refreshOutlivesTokenMs <= now;
  }

  private storeRenewal(renewed: any): string | null {
    const user = this.storedUser();
    if (!renewed?.token || !user) {
      return null;
    }
    user.token = renewed.token;
    user.refreshToken = renewed.refreshToken;
    localStorage.setItem('currentUser', JSON.stringify(user));
    return user.token;
  }

  // Another tab may have spent the refresh token first; its new token is then in localStorage.
  private tokenRenewedElsewhere(expiredToken: string): string | null {
    const token = this.storedUser()?.token;
    return token && token !== expiredToken ? token : null;
  }

  private storedUser(): any {
    return JSON.parse(localStorage.getItem('currentUser') || 'null');
  }
}
